import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import useInputState from "../../../hooks/useInputState";
import UploadHelper from "../../../helpers/upload";
import { GetAccountPreference } from "../queries";
import { UpdateAccountPreference } from "./mutation";
import DefaultToaster from "../../../atoms/DefaultToaster";
import { Intent } from "@blueprintjs/core";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useTagAccount from "../../../hooks/useTagAccount";

export function useThemeComponents() {
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [error, setError] = useState();
  const [brandLogo, setBrandLogo] = useState();
  const [backgroundImage, setBackgroundImage] = useState();
  const [headingColor, setHeadingColor] = useState("#2c2c2c");
  const [textColor, setTextColor] = useState("#8096a7");
  const [bgColor, setBgColor] = useState("#bbb");
  const [logoImageFile, setLogoImageFile] = useInputState();
  const [bgImageFile, setBgImageFile] = useInputState();
  const [backgroundOption, setBackgroundOption] = useInputState("color");
  const { data, loading } = useQuery(GetAccountPreference);
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();

  const [updateAccountPreference] = useMutation(UpdateAccountPreference, {
    refetchQueries: [{ query: GetAccountPreference }]
  });

  const { tagAccount } = useTagAccount();

  const preference = data && data.accounts[0] && data.accounts[0].preference;

  const prefillData = (preference, reset = false) => {
    const {
      backgroundImage,
      backgroundColor,
      brandLogo,
      textColor,
      headingColor
    } = preference;

    let option = backgroundImage ? "image" : "color";
    setBackgroundOption(option);

    if (option == "image") {
      backgroundImage && setBackgroundImage(backgroundImage);
      reset && setBgColor("#bbb");
    } else {
      backgroundColor && setBgColor(backgroundColor);
      reset && setBackgroundImage("");
    }
    brandLogo && setBrandLogo(brandLogo);
    textColor && setTextColor(textColor);
    headingColor && setHeadingColor(headingColor);
  };

  useEffect(() => {
    preference && prefillData(preference);
  }, [preference]);

  const onLogoChange = (file) => {
    const jsFile = new File(file, "brand_logo");
    setLogoImageFile(jsFile);
    const dataReader = new FileReader();
    dataReader.onload = (event) => {
      setBrandLogo(event.target.result);
    };
    dataReader.readAsDataURL(jsFile);
  };

  const onBackgroundChange = (file) => {
    const jsFile = new File(file, "brand_background");
    setBgImageFile(jsFile);
    const dataReader = new FileReader();
    dataReader.onload = (event) => {
      setBackgroundImage(event.target.result);
    };
    dataReader.readAsDataURL(jsFile);
  };

  const onCancel = () => {
    if (preference) {
      prefillData(preference, true);
    } else {
      setHeadingColor("#000");
      setTextColor("#000");
      setBgColor("#bbb");
      setBackgroundOption("color");
      setBrandLogo("");
      setBackgroundImage("");
    }
    DefaultToaster.show({
      message: "Form restored to original state successfully!",
      icon: "tick",
      intent: Intent.SUCCESS
    });
  };

  const saveChanges = async (e) => {
    e.preventDefault();
    let logo;
    let background;
    setIsSaving(true);
    setError(undefined);
    if (brandLogo && logoImageFile) {
      const { signedUrl, url } = await UploadHelper.getSignedUrl(
        UploadHelper.AVATAR
      );
      await UploadHelper.uploadToS3(signedUrl, logoImageFile);
      logo = url;
    }
    if (backgroundImage && bgImageFile && backgroundOption == "image") {
      const { signedUrl, url } = await UploadHelper.getSignedUrl(
        UploadHelper.AVATAR
      );
      await UploadHelper.uploadToS3(signedUrl, bgImageFile);
      background = url;
    }

    const {
      data: { updateAccountPreference: status }
    } = await updateAccountPreference({
      variables: {
        preference: {
          headingColor,
          textColor,
          brandLogo: logo,
          backgroundColor: (backgroundOption == "color" && bgColor) || "",
          backgroundImage: (backgroundOption == "image" && background) || ""
        }
      }
    });
    setIsSaving(false);
    if (status == "Updated") {
      const hasAddedCustomizedTheme = getHasCompletedTutorial(
        "Add Customized Theme"
      );

      if (!hasAddedCustomizedTheme) {
        await completeTutorial({
          variables: { name: "Add Customized Theme", version: 1 }
        });

        await tagAccount("Theme Customized");
      }

      DefaultToaster.show({
        message: "Theme settings updated!",
        icon: "tick",
        intent: Intent.SUCCESS
      });
    } else {
      setError({ message: "An error occurred while updating the record." });
    }
  };

  return {
    isPreviewOpen,
    setIsPreviewOpen,
    brandLogo,
    onLogoChange,
    backgroundImage,
    onBackgroundChange,
    saveChanges,
    onCancel,
    backgroundOption,
    setBackgroundOption,
    headingColor,
    setHeadingColor,
    textColor,
    setTextColor,
    bgColor,
    setBgColor,
    loading,
    error,
    isSaving,
    isSaveEnabled:
      !isSaving && brandLogo && (backgroundOption == "color" || backgroundImage)
  };
}
